import React, { useState, useEffect, useRef } from 'react';
import rrwebPlayer from 'rrweb-player';
import 'rrweb-player/dist/style.css';
import { useAuth } from './AuthContext';
import { BASE_URL } from '../config';

const ViewRecordings = () => {
  const { apiKey } = useAuth();
  const [operatorId, setOperatorId] = useState('');
  const [player, setPlayer] = useState(null);
  const [recording, setRecording] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  const cleanupPlayer = () => {
    try {
      if (player) {
        const replayer = player.getReplayer();
        if (replayer) {
          replayer.pause();
          replayer.destroy();
        }
      }
    } catch (_) {
      // no-op
    } finally {
      setPlayer(null);
      if (containerRef.current) containerRef.current.innerHTML = '';
    }
  };

  useEffect(() => {
    return () => {
      cleanupPlayer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    setRecording(null);
    cleanupPlayer();

    if (!operatorId) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/scout/${operatorId}/get-recording/scout`, {
        headers: {
          Authorization: `Bearer ${apiKey.key}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          typeof errorData.detail === 'string' ? errorData.detail : 'Failed to fetch recording'
        );
      }

      const data = await response.json();
      setRecording(data);

      setTimeout(() => {
        if (!containerRef.current) return;
        containerRef.current.innerHTML = '';
        try {
          const newPlayer = new rrwebPlayer({
            target: containerRef.current,
            props: {
              events: data,
              width: 800,
              height: 600,
              skipInactive: false,
              showController: true,
              autoPlay: true,
            },
          });
          setPlayer(newPlayer);
        } catch (playerErr) {
          console.error('Error initializing rrweb player:', playerErr);
          setError('Error loading recording player');
        }
      }, 50);
    } catch (err) {
      setError(err.message || 'Failed to fetch recording');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">View Scout Recordings</h1>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="operator-id">
            Operator ID
          </label>
          <div className="flex items-center gap-3">
            <input
              id="operator-id"
              type="number"
              value={operatorId}
              onChange={(e) => setOperatorId(e.target.value)}
              onFocus={() => {
                try {
                  const replayer = player?.getReplayer?.();
                  if (replayer) replayer.pause();
                } catch (_) {
                  // no-op
                }
              }}
              className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter ID"
              required
            />
            <button
              type="submit"
              className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={isLoading}
            >
              {isLoading ? 'Loading…' : 'Fetch Recording'}
            </button>
          </div>
          {error && <div className="mt-3 text-red-600">{error}</div>}
        </form>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          {!recording && !isLoading && !error && (
            <p className="text-gray-600 mb-2">Enter an operator ID and click Fetch Recording to view the session.</p>
          )}
          <div id="recording-player-view" ref={containerRef} className="w-full" tabIndex={-1} />
        </div>
      </div>
    </div>
  );
};

export default ViewRecordings;


